import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, interval, of } from "rxjs";
import { environment } from "../../../environments/environment";
import { UserService } from '../user/user.service';
import { AppConstants } from 'src/app/constants/app-constants';
import { ToastrService } from 'ngx-toastr';
import { TerminationRequest, TerminationRequestInterface } from 'src/app/model/termination-request';

/**
 * TerminationService is used to hold/move termination data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class TerminationService {
  /** A list of terminations the user has access to */
  private _terminations: TerminationRequest[] = [];
  private _terminationsSubject = new Subject<TerminationRequest[]>();
  public terminations$ = this._terminationsSubject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the termination.service, used to initialize services
   * 
   * @param http
   * @param authService 
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
    private _toastr: ToastrService,
  ) {
    // Get the terminations when the token first loads
    this._userService.token$.subscribe(
      (token: string) => {
        if (token !== "") this._fetchTerminations();
      });


    // Continuously query the backend to receive new terminations
    this._startTimer(0);
    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetchTerminations();
      });
  }

  /**
   * Get a list of terminations
   * 
   * @return A reference to the current terminations
   */
  public getTerminations(): TerminationRequest[] {
    //return this._terminations.map(x => x.copyOf());
    return this._terminations;
  }

  /**
   * Create a new termination record for supplier termination
   * 
   * @param supplierId The ID of the supplier to create the termination for
   */
  public createSupplierTermination(supplierId: number): void {
    this._http.post<TerminationRequestInterface | null>(environment.baseUrl + `/supplier-termination/${supplierId}`, {}).subscribe({
      next: (request: TerminationRequestInterface | null) => {
        if (request == null) return;

        const terminations = Array.from(this._terminations);
        terminations.push(new TerminationRequest(request));

        this._setTerminations(terminations);
      },
      error: (err: any) => {
        console.error('[TERMINATION-SERVICE]: error creating new termination record', err)
        this._toastr.error("Failed to create termination record on inactivation", "Error");
      }
    });
  }

  /**
   * Approve an termination and send it
   * 
   * @param terminationId The ID of the termination record to send
   */
  public approveTermination(terminationId: number): void {
    this._http.put<TerminationRequestInterface>(environment.baseUrl + `/supplier-termination/${terminationId}/approve`, {}).subscribe({
      next: (record: TerminationRequestInterface) => {
        // Update the termination in the list
        const index = this._terminations.findIndex(x => x.id === terminationId);
        if (index !== -1) {
          this._terminations[index].status = 'APPROVED';
          this._terminationsSubject.next(this._terminations);
        }

        this._toastr.success("Termination approved successfully!", "Success");
      },
      error: (err: HttpErrorResponse) => {
        console.error('[TERMINATION-SERVICE]: error approving termination record', err)
      }
    });
  }

  /**
   * Reject an termination
   * 
   * @param terminationId The ID of the termination record to reject
   */
  public rejectTermination(terminationId: number): void {
    this._http.delete<TerminationRequestInterface>(environment.baseUrl + `/supplier-termination/${terminationId}/reject`).subscribe({
      next: (record: TerminationRequestInterface) => {
        // Update the termination in the list
        const index = this._terminations.findIndex(x => x.id === terminationId);
        if (index !== -1) {
          this._terminations[index].status = 'REJECTED';
          this._terminationsSubject.next(this._terminations);
        }

        this._toastr.success("Termination record rejected successfully!", "Success");
      },
      error: (err: any) => {
        console.error('[TERMINATION-SERVICE]: error rejecting termination record', err)
        this._toastr.error("Failed to reject termination record", "Error");
      }
    });
  }

  /**
   * Fetch an observable of terminations
   * 
   * @return An observable containing the given terminations
   */
  public fetchTerminations(): Observable<TerminationRequest[]> {
    // If there are present terminations, return them
    if (this._terminations.length !== 0) {
      return of(this._terminations);
    }

    // Otherwise, create a copy of the termination observable that can be piped
    return this._terminationsSubject.asObservable();
  }

  /**
   * Set a list of terminations
   * 
   * @param terminations
   */
  private _setTerminations(terminations: TerminationRequest[]): void {
    this._terminations = terminations.map(x => x.copyOf());
    this._terminationsSubject.next(terminations.map(x => x.copyOf()));
  }


  /**
   * Get terminations from the backend. You should subscribe to the 'terminations$'
   * field to receive updates
   */
  private _fetchTerminations(): void {
    this._http.get(environment.baseUrl + "/supplier-terminations").subscribe({
      next: (data: Object): void => {
        // Cast the data into a list of termination interfaces
        const terminationInterfaces = data as TerminationRequestInterface[];
        if (terminationInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into termination list`);
        }

        // Convert the interfaces to class values
        let terminations: TerminationRequest[] = [];
        for (let intf of terminationInterfaces) {
          terminations.push(new TerminationRequest(intf));
        }

        // Propagate changes if different
        this._setTerminations(terminations);
        console.log(`[TERMINATION-SERVICE]: successfully queried terminations`);
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${(""+seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

const arraysEqual = (a: TerminationRequest[], b: TerminationRequest[]): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (var i = 0; i < a.length; ++i) {
    if ((a[i] === null && a[i] !== b[i]) || !a[i].equals(b[i])) return false;
  }
  return true;
}

const delay = (delay: number) => {
  return new Promise(r => {setTimeout(r, delay)});
}
